import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Zap, PenLine, FileUp, Check, Info } from "lucide-react";
import { addDays } from "date-fns";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, SkeletonRows } from "@/components/common";
import { CountdownPreview, DetailsStep, AlertsStep, FilesStep, ReviewStep } from "@/components/ReminderSteps";
import { todayIn, nextOccurrence } from "@/lib/dates";
import { isoOf, toDate } from "@/lib/brand";
import { VoiceButton } from "@/components/VoiceReminder";

const blank = (prefs) => ({
  title: "", category_id: "cat_19", subcategory_id: "cat_19_sub_01", event_date: "", date_kind: "all_day", event_time: "", time_zone: "",
  recurrence: "one_time", leap_day_policy: "feb28", alert_offsets: prefs?.default_offsets || [30, 7, 1], custom_fields: {}, owner_id: "",
  tagsText: "", notes: "", favorite: false, status: "active", cost_amount: "", cost_currency: "",
});

function Choice({ onPick }) {
  const opts = [
    { k: "quick", icon: Zap, t: "Quick Add", d: "Just a title and a date. Done in seconds." },
    { k: "manual", icon: PenLine, t: "Manual Entry", d: "Guided form with category-specific fields, alerts and files." },
    { k: "upload", icon: FileUp, t: "Upload Document", d: "Attach the document first, then review and enter its details." },
  ];
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="add-choice">
      <VoiceButton variant="card" testid="add-choice-voice" />
      {opts.map((o) => (
        <button key={o.k} onClick={() => onPick(o.k)} className="pm-card p-7 text-left hover:-translate-y-1 hover:border-[#CFC8EA] transition-transform" data-testid={`add-choice-${o.k}`}>
          <span className="w-12 h-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--pm-gradient)" }}><o.icon className="w-5 h-5" /></span>
          <h3 className="font-bold text-lg mt-5">{o.t}</h3>
          <p className="text-sm text-[var(--pm-muted)] mt-1">{o.d}</p>
        </button>
      ))}
      <p className="sm:col-span-2 xl:col-span-4 text-xs text-[var(--pm-muted)] flex items-center gap-2"><Info className="w-4 h-4" /> Automatic reading of document details is coming later — today you enter the details yourself.</p>
    </div>
  );
}

function QuickAdd({ onSaved }) {
  const [t, setT] = useState("");
  const [d, setD] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({});
  const save = async (e) => {
    e.preventDefault();
    const v = {};
    if (!t.trim()) v.title = "Title is required";
    if (!d) v.date = "Date is required";
    setErr(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    try { const { data } = await api.post("/reminders", { title: t.trim(), event_date: d }); toast.success("Reminder pinned"); onSaved(data.id); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="pm-card p-6 sm:p-8 max-w-xl grid gap-5" data-testid="quick-add-form">
      <Field label="Title" required error={err.title} htmlFor="q-t"><input id="q-t" className="pm-input" value={t} onChange={(e) => setT(e.target.value)} placeholder="e.g. Car service" data-testid="quick-title-input" /></Field>
      <Field label="Date" required error={err.date} htmlFor="q-d"><input id="q-d" type="date" className="pm-input" value={d} onChange={(e) => setD(e.target.value)} data-testid="quick-date-input" /></Field>
      <p className="text-xs text-[var(--pm-muted)]">Saved under Custom with your default alerts. You can add details later.</p>
      <button className="pm-btn pm-btn-primary" disabled={busy} data-testid="quick-save-btn">{busy ? "Saving…" : "Save reminder"}</button>
    </form>
  );
}

export default function AddReminder({ edit }) {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { meta, entities } = useMeta();
  const [mode, setMode] = useState(edit ? "manual" : sp.get("mode") || null);
  const [form, setForm] = useState(blank(user?.prefs));
  const [files, setFiles] = useState([]);
  const [existing, setExisting] = useState([]);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!edit);
  const today = todayIn(user?.prefs?.timezone);

  useEffect(() => {
    if (!edit) return;
    api.get(`/reminders/${id}`).then(({ data }) => {
      setForm({ ...blank(), ...data, owner_id: data.owner_id || "", event_time: data.event_time || "", time_zone: data.time_zone || "",
        cost_amount: data.cost_amount ?? "", cost_currency: data.cost_currency || "", tagsText: (data.tags || []).join(", ") });
      setLoaded(true);
    }).catch(() => { toast.error("Reminder not found"); nav("/reminders"); });
    api.get("/attachments", { params: { reminder_id: id } }).then(({ data }) => setExisting(data.items)).catch(() => {});
  }, [edit, id, nav]);

  const steps = useMemo(() => (mode === "upload" ? ["Upload", "Details", "Notifications", "Review & Save"] : ["Details", "Notifications", "Attachments", "Review & Save"]), [mode]);
  const cur = steps[step];
  const sub = meta?.categories.find((c) => c.id === form.category_id)?.subcategories.find((s) => s.id === form.subcategory_id);
  const fieldSet = meta?.field_sets[sub?.field_set || "generic"];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCustom = (k, v) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [k]: v } }));
  const applySub = (subId, catId = form.category_id) => {
    const c = meta.categories.find((x) => x.id === catId);
    const s = c.subcategories.find((x) => x.id === subId) || c.subcategories[0];
    const fs = meta.field_sets[s.field_set] || meta.field_sets.generic;
    setForm((f) => ({ ...f, category_id: catId, subcategory_id: s.id, recurrence: fs.recurrence, alert_offsets: fs.offsets, date_kind: fs.date_kind || "all_day",
      title: f.title || (fs.fields.length ? s.name : f.title) }));
  };

  const validateDetails = () => {
    const v = {};
    if (!form.title.trim()) v.title = "Title is required";
    if (!form.event_date) v.event_date = "Date is required";
    if (form.cost_amount !== "" && Number(form.cost_amount) < 0) v.cost_amount = "Must be positive";
    if (form.cost_currency && !/^[A-Za-z]{3}$/.test(form.cost_currency)) v.cost_currency = "3 letters";
    setErrors(v);
    return !Object.keys(v).length;
  };
  const next = () => {
    if (cur === "Details" && !validateDetails()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!validateDetails()) { setStep(steps.indexOf("Details")); return; }
    setBusy(true);
    const nd = nextOccurrence(form.event_date, form.recurrence, today, form.leap_day_policy);
    const offsets = form.alert_offsets.filter((o) => isoOf(addDays(toDate(nd), -o)) >= today || form.recurrence !== "one_time");
    const payload = {
      ...form, alert_offsets: offsets, owner_id: form.owner_id || null, event_time: form.date_kind === "instant" ? form.event_time || null : null,
      time_zone: form.date_kind === "instant" ? form.time_zone || user?.prefs?.timezone : null,
      cost_amount: form.cost_amount === "" ? null : Number(form.cost_amount), cost_currency: form.cost_currency || null,
      tags: form.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      const { data } = edit ? await api.put(`/reminders/${id}`, payload) : await api.post("/reminders", payload);
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const { data: up } = await api.post(`/reminders/${data.id}/attachments`, fd);
        if (up.errors.length) toast.warning(`${up.errors.length} file(s) not uploaded: ${up.errors.map((e) => `${e.file} (${e.error})`).join(", ")}`);
        else toast.success(`${up.saved.length} file${up.saved.length > 1 ? "s" : ""} attached`);
      }
      toast.success(edit ? "Reminder updated" : "Reminder saved");
      nav(`/reminders/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const removeExisting = async (a) => {
    await api.delete(`/attachments/${a.id}`).then(() => setExisting(existing.filter((x) => x.id !== a.id))).catch(() => toast.error("Could not remove file"));
  };

  if (!meta || !loaded) return <SkeletonRows rows={6} />;
  const title = edit ? `Edit — ${form.title}` : mode ? `Add Reminder${sub && mode !== "quick" ? ` — ${sub.name}` : ""}` : "Add Reminder";

  return (
    <div data-testid="add-reminder-page">
      <PageHeader title={title} subtitle="Organize, track and stay ahead of what matters." actions={mode && !edit && <button className="pm-btn pm-btn-outline" onClick={() => { setMode(null); setStep(0); }} data-testid="change-mode-btn">Change method</button>} />
      {!mode && <Choice onPick={(m) => { setMode(m); setStep(0); }} />}
      {mode === "quick" && <QuickAdd onSaved={(rid) => nav(`/reminders/${rid}`)} />}
      {(mode === "manual" || mode === "upload") && (
        <>
          <ol className="flex flex-wrap gap-x-6 gap-y-2 mb-6" data-testid="wizard-steps">
            {steps.map((s, i) => (
              <li key={s} className={`flex items-center gap-2 text-sm font-semibold ${i === step ? "text-[var(--pm-primary)]" : "text-[var(--pm-muted)]"}`}>
                <span className={`w-7 h-7 rounded-full grid place-items-center text-xs ${i < step ? "bg-[var(--pm-success)] text-white" : i === step ? "bg-[#EFEBFF]" : "bg-[#F1EFF6]"}`}>{i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</span>{s}
              </li>
            ))}
          </ol>
          <div className="grid xl:grid-cols-[1.5fr_1fr] gap-6 items-start">
            <div className="pm-card p-5 sm:p-7">
              {cur === "Upload" && (
                <>
                  <FilesStep files={files} setFiles={setFiles} />
                  <p className="text-xs text-[var(--pm-muted)] mt-4 flex items-center gap-2"><Info className="w-4 h-4" /> Next, you'll review and enter the document details manually. Automatic reading of documents is not active yet.</p>
                </>
              )}
              {cur === "Details" && <DetailsStep form={form} set={set} setCustom={setCustom} meta={meta} entities={entities} errors={errors}
                onCategory={(cid) => applySub(meta.categories.find((c) => c.id === cid).subcategories[0].id, cid)} onSub={(sid) => applySub(sid)} />}
              {cur === "Notifications" && <AlertsStep form={form} setOffsets={(o) => setForm((f) => ({ ...f, alert_offsets: o }))} today={today} emailEnabled={user?.prefs?.email_enabled} />}
              {cur === "Attachments" && <FilesStep files={files} setFiles={setFiles} existing={existing} onRemoveExisting={edit ? removeExisting : null} />}
              {cur === "Review & Save" && <ReviewStep form={form} meta={meta} entities={entities} files={files} today={today} />}
              <div className="flex justify-between mt-8 pt-5 border-t border-[var(--pm-border)]">
                {step === 0 ? <Link to={edit ? `/reminders/${id}` : "/reminders"} className="pm-btn pm-btn-outline" data-testid="wizard-cancel-btn"><ArrowLeft className="w-4 h-4" /> Cancel</Link>
                  : <button className="pm-btn pm-btn-outline" onClick={() => setStep(step - 1)} data-testid="wizard-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>}
                {step < steps.length - 1 ? <button className="pm-btn pm-btn-primary" onClick={next} data-testid="wizard-next-btn">Next: {steps[step + 1]} <ArrowRight className="w-4 h-4" /></button>
                  : <button className="pm-btn pm-btn-primary" onClick={save} disabled={busy} data-testid="wizard-save-btn">{busy ? "Saving…" : edit ? "Save changes" : "Save reminder"}</button>}
              </div>
            </div>
            <aside className="pm-card p-7 bg-gradient-to-br from-white to-[#FFF6FA] xl:sticky xl:top-24"><CountdownPreview form={form} today={today} fieldSet={fieldSet} /></aside>
          </div>
        </>
      )}
    </div>
  );
}
